import React, { useState } from 'react';
import styled from 'styled-components';
import { IStore } from '../../../shared/types/Store';
import { useStatus } from '../../../shared/contexts/StatusContext';
import { useStore } from '../../../shared/contexts/StoreContext';
import { ClosedModal } from '../../../shared/components/ClosedModal';
import { useImageUrl } from '../../../shared/hooks/useImageUrl';

const DeliveryTimeBadge = styled.div`
  position: absolute;
  top: 14px;
  right: 60px;
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.2);
  padding: 7px 11px;
  border-radius: 20px;
  color: white;
  font-size: 0.72rem;
  font-weight: 700;
  z-index: 10;
`;

const ShareBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 16px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  border: none;
  cursor: pointer;
  color: white;
  transition: background 0.2s;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  &:active {
    background: rgba(255, 255, 255, 0.4);
  }
`;

const Container = styled.div`
  background-color: var(--primary);
  color: white;
  padding: 24px 24px 32px;
  border-bottom-left-radius: 3rem;
  border-bottom-right-radius: 3rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 140px;

  @media (min-width: 1024px) {
    height: 220px;
    padding: 40px;
  }
`;

const TopBar = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
`;

const StatusIndicator = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 14px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const StatusDot = styled.span<{ $isOpen: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$isOpen ? '#10b981' : '#ef4444'};
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  animation: ${props => props.$isOpen ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
    }
    50% {
      box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.3);
    }
  }
`;

const StatusText = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  text-transform: uppercase;
`;

const Logo = styled.img<{ $size?: number }>`
  height: auto;
  max-height: ${props => props.$size ? `${props.$size}px` : '100px'};
  width: 100%;
  max-width: ${props => props.$size ? `${props.$size}px` : '100px'};
  margin-top: 20px;
  object-fit: contain;
  border-radius: 12px;

  @media (min-width: 1024px) {
    max-height: ${props => props.$size ? `${Math.round(props.$size * 1.4)}px` : '140px'};
    max-width: ${props => props.$size ? `${Math.round(props.$size * 1.4)}px` : '140px'};
  }
`;

interface HeaderProps {
    store: IStore;
}

export const Header: React.FC<HeaderProps> = ({ store }) => {
    const { isOpen, message } = useStatus();
    const { deliveryTimes } = useStore();
    const [showClosedModal, setShowClosedModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [copied, setCopied] = useState(false);

    const logoUrl = useImageUrl(store.logo);
    const storeUrl = window.location.href;

    const handleStatusClick = () => {
        if (!isOpen) setShowClosedModal(true);
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: store.name || 'Cardápio', url: storeUrl });
            } catch (_) {}
        } else {
            setShowShareModal(true);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(storeUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        });
    };

    return (
        <>
            <Container>
                <TopBar>
                    {message && (
                        <StatusIndicator onClick={handleStatusClick}>
                            <StatusDot $isOpen={isOpen} />
                            <StatusText>{message}</StatusText>
                        </StatusIndicator>
                    )}
                </TopBar>

                <Logo
                  src={logoUrl || '/img/Logo.png'}
                  alt={store.name}
                  $size={store.headerLogoSize}
                />

                {deliveryTimes.tempoMedioEntrega && (
                    <DeliveryTimeBadge>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {deliveryTimes.tempoMedioEntrega} min
                    </DeliveryTimeBadge>
                )}

                <ShareBtn onClick={handleShare}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                    </svg>
                </ShareBtn>
            </Container>

            <ClosedModal isOpen={showClosedModal} onClose={() => setShowClosedModal(false)} />

            {showShareModal && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                    onClick={() => setShowShareModal(false)}
                >
                    <div
                        style={{ background: 'white', borderRadius: '20px', padding: '24px', maxWidth: '340px', width: '100%' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ fontSize: '1rem', fontWeight: 800, color: '#1f2937', marginBottom: '6px' }}>Compartilhar cardápio</div>
                        <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '16px', lineHeight: 1.5 }}>
                            Copie o link e mande para quem quiser indicar.
                        </p>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                            <div style={{ flex: 1, background: '#f3f4f6', borderRadius: '10px', padding: '10px 12px', fontSize: '0.78rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {storeUrl}
                            </div>
                            <button
                                onClick={handleCopy}
                                style={{ background: copied ? '#10b981' : 'var(--primary, #f58220)', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 16px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.2s' }}
                            >
                                {copied ? 'Copiado!' : 'Copiar'}
                            </button>
                        </div>
                        <button
                            onClick={() => setShowShareModal(false)}
                            style={{ width: '100%', padding: '11px', borderRadius: '10px', background: '#f3f4f6', color: '#374151', border: 'none', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};
